import { z } from 'zod';

const rangeTypeSchema = z.object({
  from: z.number(),
  to: z.number().nullable(),
});
const primitiveTypeSchema = z.union([z.number(), z.string(), z.null()]);
const fractionTypeSchema = z.object({ numerator: z.number(), denominator: z.number() });
const fieldTypeSchema = z.union([primitiveTypeSchema, fractionTypeSchema, rangeTypeSchema]);
const rowTypeSchema = z.record(z.string(), fieldTypeSchema);
const summaryRowSchema = z
  .record(z.string(), z.union([z.number(), z.string(), z.null(), fractionTypeSchema]))
  .nullable();
const timeIntervalSchema = z.enum(['hour', 'day', 'week', 'month', 'day_of_week', 'hour_of_day']);
const apiVisualizationTypeSchema = z.enum([
  'bar',
  'horizontal-bar',
  'tabular',
  'area',
  'line',
  'column-line',
  'donut',
  'counter',
  'heatmap',
  'banner',
  'data-table',
]);
const visualizationTypeSchema = apiVisualizationTypeSchema.transform((apiType) => {
  const visualizationTypeMap = {
    'horizontal-bar': 'bar',
    bar: 'column',
    tabular: 'tabular',
    area: 'area',
    line: 'line',
    'column-line': 'combo',
    donut: 'pie',
    counter: 'counter',
    heatmap: 'heatmap',
    banner: 'banner',
    'data-table': 'data-table',
  };
  return visualizationTypeMap[apiType];
});

const timezoneSchema = z.object({
  name: z.string(),
  ianaName: z.string(),
});

const fractionUnitSchema = z
  .object({
    numerator: z.enum(['value']),
    denominator: z.enum(['value', 'seconds']),
  })
  .optional();

const columnDefinitionSchema = z.object({
  position: z.number(),
  unit: z.string(),
  fieldType: z.enum(['value', 'fraction', 'range', 'text', 'number']),
  fractionUnit: fractionUnitSchema,
  improvementDirection: z.enum(['increasing', 'decreasing']).optional(),
});

const headerDefinitionSchema = z.object({
  value: z.union([z.string(), z.number()]),
  unit: z.enum(['text', 'timestamp', 'number']),
});

const tabularMetricValueSchema = z.object({
  value: z.number().nullable(),
  numeratorValue: z.number().nullable().optional(),
  denominatorValue: z.number().nullable().optional(),
});

const tabularDimensionValueSchema = z.object({
  kind: z.literal('dimension'),
  rawValue: z.union([
    z.number(),
    z.string(),
    z.boolean(),
    z.null(),
    z.record(z.unknown()),
    z.array(z.unknown()),
  ]),
  displayValue: z.union([z.number(), z.string(), z.boolean(), z.null()]),
  resolvedValue: z.record(z.unknown()).optional(),
});

const tabularBucketEntrySchema = z
  .object({
    key: z.union([z.string(), z.number()]).optional(),
    from: z.number().nullable().optional(),
    to: z.number().nullable().optional(),
    doc_count: z.number().nullable().optional(),
  })
  .passthrough();

const tabularDistributionValueSchema = z.array(tabularBucketEntrySchema);

const tabularCellSchema = z.union([
  primitiveTypeSchema,
  rangeTypeSchema,
  tabularMetricValueSchema,
  tabularDimensionValueSchema,
  tabularDistributionValueSchema,
]);

const tabularColumnSchema = z.string();

const tabularRowSchema = z.union([z.array(tabularCellSchema), tabularCellSchema]);

const tabularMetricMetadataSchema = z
  .object({
    metricId: z.string(),
    improvementDirection: z.enum(['increasing', 'decreasing']).default('increasing'),
    fractionUnit: fractionUnitSchema.optional(),
    unit: z
      .enum(['value', 'percent', 'seconds', 'minutes', 'hours', 'ratio', 'score'])
      .default('value'),
  })
  .passthrough();

const tabularMetadataSchema = z
  .object({
    columns: z.array(tabularColumnSchema),
    metricMetadata: z.array(tabularMetricMetadataSchema).default([]),
    summaryRow: z
      .union([tabularRowSchema, z.array(z.array(tabularCellSchema))])
      .nullable()
      .optional(),
  })
  .passthrough();

const tabularSchema = z.object({
  period: z.enum(['current', 'previous']),
  data: z.array(tabularRowSchema),
  metadata: tabularMetadataSchema,
});

const chartSchema = z.object({
  headers: z.array(headerDefinitionSchema).nullable(),
  columnDefinitions: z.array(columnDefinitionSchema),
  values: z.array(rowTypeSchema),
  period: z.number().nullable(),
  summaryRow: summaryRowSchema,
});

const chartSchemasEntrySchema = z.object({
  chartId: z.number(),
  schemaVersion: z.number().default(1),
  schemas: z.array(chartSchema),
  tabularSchemas: z.array(tabularSchema).default([]),
});

const chartSeriesSchema = z.object({
  metricId: z.string(),
  metricDisplayName: z.string().optional().nullable(),
  aggregation: z.string().optional().nullable(),
  label: z.string().nullable().optional(),
  showDataLabels: z.boolean().optional().default(false),
});

const bannerBlockSchema = z.object({
  type: z.enum(['paragraph', 'heading', 'subheading', 'subheading3']),
  text: z.string(),
  class: z.string().optional(),
  align: z.enum(['left', 'center', 'right', 'justify']).optional(),
  idAttribute: z.string().optional(),
});

const bannerComponentSchema = z.object({
  color: z.string().optional(),
  border_color: z.string().optional(),
  is_migration_banner: z.boolean().optional(),
  blocks: z
    .array(bannerBlockSchema)
    .nullable()
    .optional()
    .transform((blocks) => blocks ?? []),
});

const visualizationOptionsSchema = z
  .object({
    showRelativeValues: z.boolean().default(false),
    showSummaryRow: z.boolean().default(false),
    showTimeComparison: z.boolean().default(false),
    target: z
      .object({
        value: z.number(),
      })
      .optional(),
    component: bannerComponentSchema.optional(),
  })
  .nullable()
  .transform((options) => {
    if (options === null) {
      return {
        showRelativeValues: false,
        showSummaryRow: false,
        showTimeComparison: false,
      };
    }
    return options;
  });

const chartDefinitionSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable().optional(),
  viewBy: z.string().nullable(),
  viewByDisplay: z.string().nullable().optional(),
  viewByTimeInterval: timeIntervalSchema.nullable(),
  segmentBy: z.string().nullable(),
  segmentByDisplay: z.string().nullable().optional(),
  segmentByTimeInterval: timeIntervalSchema.nullable(),
  visualizationType: visualizationTypeSchema,
  stacked: z.boolean(),
  chartSeries: z.array(chartSeriesSchema),
  visualizationOptions: visualizationOptionsSchema,
  gridPositionX: z.number(),
  gridPositionY: z.number(),
  gridWidth: z.number(),
  gridHeight: z.number(),
});

const snapshotFilterSchema = z.object({
  label: z.string(),
  operator: z.string(),
  relation: z.string(),
  values: z.array(z.string()),
  attribute_type: z.string().optional(),
  attribute_group: z.string().optional(),
  property_field: z.string().optional(),
});

const snapshotFiltersSchema: z.ZodType<{
  type: 'and' | 'or';
  filters: (SnapshotFilter | SnapshotFilters)[];
}> = z.lazy(() =>
  z.object({
    type: z.enum(['and', 'or']),
    filters: z.array(z.union([snapshotFilterSchema, snapshotFiltersSchema])),
  }),
);

const dateRange = z
  .object({
    selection: z.string(),
    start: z.string().nullable(),
    end: z.string().nullable(),
  })
  .nullable();

const reportSchema = z.object({
  id: z.string(),
  title: z
    .string()
    .nullable()
    .transform((title) => title ?? 'Untitled'),
  description: z.string().nullable(),
  dateRange: dateRange,
  timezone: timezoneSchema,
  charts: z.array(chartDefinitionSchema),
  snapshotFilters: snapshotFiltersSchema
    .optional()
    .nullable()
    .default({ type: 'and', filters: [] }),
});

const sharedBySchema = z.object({
  name: z.string().nullable(),
  avatar: z.string(),
});

export const reportSnapshotSchema = z.object({
  report: reportSchema,
  chartSchemas: z.array(chartSchemasEntrySchema),
  sharedBy: sharedBySchema,
  expiresAt: z.number(),
});

export type ChartSchema = z.infer<typeof chartSchema>;
export type ReportChartSchema = z.infer<typeof chartSchemasEntrySchema>;
export type CellType = z.infer<typeof fieldTypeSchema>;
export type ColumnDefinition = z.infer<typeof columnDefinitionSchema>;
export type ChartDefinition = z.infer<typeof chartDefinitionSchema>;
export type Report = z.infer<typeof reportSchema>;
export type ReportSnapshot = z.infer<typeof reportSnapshotSchema>;
export type RowType = z.infer<typeof rowTypeSchema>;
export type TimeInterval = z.infer<typeof timeIntervalSchema>;
export type RangeType = z.infer<typeof rangeTypeSchema>;
export type Timezone = z.infer<typeof timezoneSchema>;
export type HeaderDefinition = z.infer<typeof headerDefinitionSchema>;
export type SummaryRow = z.infer<typeof summaryRowSchema>;
export type DateRange = z.infer<typeof dateRange>;

export type SnapshotFilter = z.infer<typeof snapshotFilterSchema>;
export interface SnapshotFilters {
  type: 'and' | 'or';
  filters: (SnapshotFilter | SnapshotFilters)[];
}
export type FractionUnit = z.infer<typeof fractionUnitSchema>;
export type BannerBlock = z.infer<typeof bannerBlockSchema>;
export type BannerComponent = z.infer<typeof bannerComponentSchema>;
export type TabularCell = z.infer<typeof tabularCellSchema>;
export type TabularSchema = z.infer<typeof tabularSchema>;
export type TabularColumn = z.infer<typeof tabularColumnSchema>;
export type TabularMetadata = z.infer<typeof tabularMetadataSchema>;
export type TabularMetricMetadata = z.infer<typeof tabularMetricMetadataSchema>;
export type TabularMetricValue = z.infer<typeof tabularMetricValueSchema>;
export type TabularDimensionValue = z.infer<typeof tabularDimensionValueSchema>;
export type TabularDistributionValue = z.infer<typeof tabularDistributionValueSchema>;
export type TabularRow = z.infer<typeof tabularRowSchema>;
